import { Button, Icon } from 'rsuite';
import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useGoogleLogin } from '@react-oauth/google';
import { get } from 'lodash/fp';
import { getIsSaving } from '../../../../redux/Settings/selectors/portal/smtp-account';
import {
  createSmtpAccount,
  updateSmtpAccount
} from '../../../../redux/Settings/sagas/portal/smtp-account/routines';
import { SmtpAccountType } from '../../../../interfaces/smtpAccount';
import {
  SmtpAccountConnectionState,
  SmtpAccountStatus
} from '../../../../constants/smtp-account';

const GMAIL_SCOPES = [
  'https://www.googleapis.com/auth/userinfo.profile',
  'https://www.googleapis.com/auth/gmail.metadata',
  'https://www.googleapis.com/auth/gmail.send'
];

const GMAIL_REDIRECT_URI = `${window.location.origin}/smtp-callback/gmail/`;

const SmtpAccountSubmitButton = ({ formValue, data, disabled = false }) => {
  const dispatch = useDispatch();
  const isSaving = useSelector(getIsSaving);
  const { isConnected } = data;

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    scope: GMAIL_SCOPES.join(' '),
    ux_mode: 'redirect',
    redirect_uri: GMAIL_REDIRECT_URI
  });

  const handleSubmit = async () => {
    const { id = null, ...body } = formValue;

    if (id) {
      const { clientId, clientSecret } = data;
      const disConnected =
        isConnected === SmtpAccountConnectionState.DISCONNECTED;
      const clientIdOrSecretChanged =
        clientId !== get('clientId', formValue) ||
        clientSecret !== get('clientSecret', formValue);
      const proceedGoogleLogin = disConnected || clientIdOrSecretChanged;

      const updateData = {
        id,
        body: {
          ...body,
          // Force to disconnect if clientIdOrSecretChanged
          // It will be connected only in a successful google redirect callback
          ...(proceedGoogleLogin && {
            status: SmtpAccountStatus.INACTIVE,
            isConnected: SmtpAccountConnectionState.DISCONNECTED
          })
        }
      };

      await dispatch(updateSmtpAccount(updateData));
      // Redirect to google login if clientId or secret was changed
      if (proceedGoogleLogin) googleLogin();
    } else {
      await dispatch(createSmtpAccount(body));
      // Always redirect to google login if create
      googleLogin();
    }
  };

  return (
    <Button
      appearance="primary"
      onClick={handleSubmit}
      disabled={isSaving || disabled}
    >
      <Icon icon="save" /> {isSaving ? 'Submitting...' : 'Submit'}
    </Button>
  );
};

SmtpAccountSubmitButton.propTypes = {
  data: SmtpAccountType,
  formValue: SmtpAccountType,
  disabled: PropTypes.bool
};

SmtpAccountSubmitButton.defaultProps = {
  data: {},
  formValue: {},
  disabled: false
};

export default SmtpAccountSubmitButton;
